import { pick } from 'lodash';
import { isAxiosError } from 'axios';
import { ApiError, AppError, ErrorCode } from '@/types/ErrorCodes';
import { formatDistance } from 'date-fns';
import { BaseEntity } from '@/api/EntityApi';
import {
	AvailableAttributeValues,
	CreatorActionType,
	Language,
	Translations,
} from '@/types/types';
import { pl } from 'date-fns/locale';
import { MapType } from '@/api/AssetApi';
import { AttributeViewType } from '@/api/AttributeApi';
import { AccessoryRelationAction } from '@/api/AccessoryApi';
import { ProductType } from '@/api/ProductTypeApi';
import APP_CONFIG from '@/configs/appConfig';
import LANGUAGES from '@/configs/languages';
import moment from 'moment';

export const priceFormatter = new Intl.NumberFormat('PL-pl', {
	style: 'currency',
	currency: 'PLN',
	currencyDisplay: 'code',
});

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD/MM/YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function sortByDate(
	dateA: moment.MomentInput,
	dateB: moment.MomentInput,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function sortByName(
	nameA = '',
	nameB = '',
	order: 'asc' | 'desc' = 'asc'
) {
	return order === 'asc'
		? nameA.localeCompare(nameB)
		: nameB.localeCompare(nameA);
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isAppError = (error: unknown): error is AppError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as AppError).message
	);
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
};

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateFromNow(date: Date) {
	return formatDistance(date, new Date(), {
		locale: pl,
		includeSeconds: true,
		addSuffix: true,
	});
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1000000 + Number.EPSILON) * 100) / 100;
}

export function extractId<TData extends BaseEntity>(entity: TData) {
	return pick(entity, 'id');
}

export function mapMapTypeName(mapType: MapType) {
	switch (mapType) {
		case MapType.BASE:
			return 'Podstawowa';
		case MapType.ROUGHNESS:
			return 'Szorstkość';
		case MapType.NORMAL:
			return 'Normalna';
		case MapType.HEIGHT:
			return 'Wysokość';
		case MapType.METALLNESS:
			return 'Metaliczność';
		case MapType.AO:
			return 'AO';
		case MapType.LIGHT:
			return 'Światło';
	}
}

export function mapCreatorActionTypeName(type: CreatorActionType) {
	switch (type) {
		case CreatorActionType.CAMERA_FOCUS:
			return 'Ustaw kamerę';
		case CreatorActionType.SET_MATERIAL:
			return 'Ustaw materiał';
		case CreatorActionType.SET_MODEL:
			return 'Ustaw model';
		case CreatorActionType.UPDATE_MATERIAL:
			return 'Zaktualizuj materiał';
	}
}

export function getAttributeViewTypeName(viewType: AttributeViewType) {
	switch (viewType) {
		case AttributeViewType.TILES:
			return 'Kafelki';
	}
}

export function getAccessoryRelationActionName(
	relationAction: AccessoryRelationAction
) {
	switch (relationAction) {
		case AccessoryRelationAction.SHOW:
			return 'Pokaż jeżeli';
		case AccessoryRelationAction.HIDE:
			return 'Ukryj jeżeli';
	}
}

export function getAvailableAttributeValues(productType: ProductType) {
	const availableAttributeValues: AvailableAttributeValues = {
		attributes: [],
		dimensionAttributes: [],
	};

	for (const attributeValue of productType.attributeValues) {
		const availableAttributes = attributeValue.attribute.isDimension
			? availableAttributeValues.dimensionAttributes
			: availableAttributeValues.attributes;

		const attribute = availableAttributes.find(
			(attr) => attr.id === attributeValue.attribute.id
		);

		if (attribute) {
			attribute.attributeValues.push(attributeValue);
			continue;
		}

		availableAttributes.push({
			...attributeValue.attribute,
			attributeValues: [attributeValue],
		});
	}

	return availableAttributeValues;
}

export function getEmptyTranslationObject<K extends string>(
	keys: K[]
): Translations<Record<K, string>> {
	const translations = {} as Translations<Record<K, string>>;

	let language: Language;
	for (language in LANGUAGES) {
		if (language === APP_CONFIG.DEFAULT_LANGUAGE) continue;

		translations[language] = {} as Record<K, string>;

		for (const key of keys) {
			translations[language][key] = '';
		}
	}

	return translations;
}
